import React, { useState } from 'react';
import {
    Box,
    ButtonBase,
    Divider,
    IconButton,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '../theme/ThemeContext';
import { PAPER_MODES, ACCENT_COLORS } from '../theme/themePresets';

const ThemeToggle = ({ size = "small", sx = {} }) => {
    const { paperMode, accentColor, setPaperMode, setAccentColor } = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Tooltip title="阅读主题与颜色">
                <IconButton onClick={handleClick} color="inherit" size={size} sx={sx} aria-label="主题颜色">
                    <PaletteIcon fontSize={size} />
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: { xs: 300, sm: 320 },
                        borderRadius: 3,
                        boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
                        border: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    📖 8 款阅读主题
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                    {PAPER_MODES.map((mode) => {
                        const isSelected = paperMode === mode.id;
                        return (
                            <ButtonBase
                                key={mode.id}
                                onClick={() => {
                                    setPaperMode(mode.id);
                                }}
                                sx={{
                                    p: 1,
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    height: 52,
                                    bgcolor: mode.bgPaper,
                                    color: mode.textPrimary,
                                    border: '2px solid',
                                    borderColor: isSelected ? 'primary.main' : mode.divider,
                                    boxShadow: isSelected ? '0 0 0 1px currentColor' : 'none',
                                    transition: 'all 150ms ease',
                                    '&:hover': {
                                        transform: 'scale(1.02)',
                                        boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                                        {mode.nameZh}
                                    </Typography>
                                    {isSelected && (
                                        <CheckIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                                    )}
                                </Box>
                                <Typography variant="caption" sx={{ opacity: 0.65, fontSize: '0.68rem' }}>
                                    恩典 · 平安
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Box>

                <Divider sx={{ my: 1.75 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                    🎨 8 款强调色调
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                    {ACCENT_COLORS.map((accent) => {
                        const isSelected = accentColor === accent.id;
                        return (
                            <Tooltip key={accent.id} title={accent.nameZh} placement="top">
                                <IconButton
                                    size="small"
                                    onClick={() => setAccentColor(accent.id)}
                                    sx={{
                                        p: 0.4,
                                        border: '2px solid',
                                        borderColor: isSelected ? accent.dotColor : 'transparent',
                                        transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                                        transition: 'all 150ms ease',
                                        '&:hover': { transform: 'scale(1.25)' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            bgcolor: accent.dotColor,
                                            boxShadow: isSelected ? `0 0 8px ${accent.dotColor}` : 'none',
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Popover>
        </>
    );
};

export default ThemeToggle;
 